import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import commentReportService from "@/services/api/commentReports";
import type { FlaggedComment, GetCommentReportsParams } from "@/services/api/commentReports";

export interface CommentReportState {
  commentReports: FlaggedComment[];
  totalCommentReports: number;
  isFetching: boolean;
  error: string | null;
}

const initialState: CommentReportState = {
  commentReports: [],
  totalCommentReports: 0,
  isFetching: false,
  error: null,
};

export const getCommentReports = createAsyncThunk(
  "commentReport/getCommentReports",
  async (params: GetCommentReportsParams | undefined) => commentReportService.getCommentReports(params),
);

export const hideComment = createAsyncThunk("commentReport/hideComment", async (commentId: number) => {
  await commentReportService.hideComment(commentId);
  return commentId;
});

// A comment can carry more than one report (see `FlaggedComment.reports`)
// — this page shows one aggregate row per comment, not per report, so
// "Dismiss" on that row clears every report against the comment, not
// just one. Otherwise the row would just reappear on the next fetch with
// a smaller `reportCount` instead of actually going away.
export const dismissComment = createAsyncThunk("commentReport/dismissComment", async (comment: FlaggedComment) => {
  await Promise.all(comment.reports.map((report) => commentReportService.dismissReport(report.reportId)));
  return comment.commentId;
});

const commentReportSlice = createSlice({
  name: "commentReport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCommentReports.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getCommentReports.fulfilled, (state, action) => {
        state.isFetching = false;
        state.commentReports = action.payload?.data?.results ?? [];
        state.totalCommentReports = action.payload?.data?.totalRecord ?? state.commentReports.length;
      })
      .addCase(getCommentReports.rejected, (state, action) => {
        state.isFetching = false;
        state.commentReports = [];
        state.error = action.error.message ?? "Failed to load flagged comments";
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        state.commentReports = state.commentReports.filter((comment) => comment.commentId !== action.payload);
        state.totalCommentReports = Math.max(0, state.totalCommentReports - 1);
      })
      .addCase(dismissComment.fulfilled, (state, action) => {
        state.commentReports = state.commentReports.filter((comment) => comment.commentId !== action.payload);
        state.totalCommentReports = Math.max(0, state.totalCommentReports - 1);
      });
  },
});

export default commentReportSlice.reducer;
